import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, assetUrl } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { TEAM_INFO } from "../../lib/teamMap";
import { useTeamLookup } from "../../lib/teamLookup";
import { Loader, SectionHeading } from "../../components/Loader";
import { ImageUpload } from "../../components/ImageUpload";
import { HomeHero, DEFAULT_HERO_CONFIG, DEFAULT_QUICK_LINKS, HERO_LOGO_PRESETS } from "../../components/HomeHero";
import { Trash2, Plus, X, Save, AlertCircle, Settings as SettingsIcon, Star, SlidersHorizontal } from "lucide-react";
import { savePwhlStatMode, savePublicStatSource, saveLogoMode } from "../../lib/api";

import { PWHL_SKATER_STAT_COLS, PWHL_GOALIE_STAT_COLS } from "../../lib/pwhlStatColumns";

import {
    TEAM_IDS, ACTIVE_PWHL_TEAM_IDS, PWHL_TEAM_IDS,
    calculateAge, COACH_ROLES, COACH_LEAGUES, STAFF_SEASON_OPTIONS,
    CONTRACT_STATUS, LEAGUES, POSITIONS, CURRENT_SEASON,
    formatMoney, parseMoney, MoneyInput,
    confirmDelete, seasonStatus, confirmPassword,
    SEASON_OPTIONS, ContractBadge,
    blankSkaterRow, blankGoalieRow, blankSalary, blankCareer, blankPlayer, blankCoach,
    COACH_YEARS,
    inputCls, Field, buildSeasonRange, STAT_SEASON_OPTIONS, NCAA_SCHOOL_OPTIONS,
    StatEditor, SalaryEditor, CareerEditor, StaffHistoryEditor, AwardsEditor,
    TEAM_FIRST_SEASON, teamEligibleForSeason, teamsForSeason, activeTeamsForSeason,
    PWHL_SEASONS, PWHL_STAND_BLANK,
    todayStr,
} from "./shared";

const ALL_SEASON_OPTIONS = (() => {
    const arr = [];
    for (let y = 2030; y >= 2000; y--) arr.push(`${y}-${String(y + 1).slice(2)}`);
    return arr;
})();

const HERO_STATS_DEFAULT = [
    { label: "Players", link: "/leaderboards", source: "players", value: "" },
    { label: "Teams", link: "/teams", source: "teams", value: "" },
    { label: "Games", link: "/teams?tab=schedule", source: "games", value: "" },
    { label: "Records", link: "/records", source: "custom", value: "100+" },
];

const HERO_STAT_SOURCES = [
    { k: "players", l: "Live: Players" },
    { k: "teams", l: "Live: Teams" },
    { k: "games", l: "Live: Games" },
    { k: "custom", l: "Custom text" },
];


const LogoSlider = ({ label, testId, min, max, step, value, display, onChange }) => (
    <div>
        <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</span>
            <span className="text-[10px] font-mono-stat text-white">{display}</span>
        </div>
        <input
            type="range"
            min={min} max={max} step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full accent-[#9D4CDD] cursor-pointer"
            data-testid={testId}
        />
    </div>
);

function SettingsAdmin() {
    const qc = useQueryClient();
    const [form, setForm] = useState({
        current_season: "2025-26",
        next_season: "2026-27",
        season_status: "Offseason",
        current_stage: "regular",
        team_bar_position: "subnav",
        expansion_now: false,
        site_logo_url: "",
        logo_x: 50,
        logo_y: 50,
        logo_zoom: 1,
        logo_w: 96,
        logo_h: 96,
        site_name: "Rosterly",
        site_tagline: "Women's Hockey",
        hero_title: "",
        hero_title_colors: [],
        hero_subtitle: "",
        hero_image_url: "",
        hero_variant: "original",
        hero_stats: [],
        hero_config: DEFAULT_HERO_CONFIG,
        quick_links: [],
        instagram_url: "",
        beta_mode: true,
        beta_message: "",
    });
    const [savedAt, setSavedAt] = useState("");
    const [error, setError] = useState("");

    const { data, isLoading } = useQuery({
        queryKey: ["settings"],
        queryFn: () => api.get("/settings").then((r) => r.data),
    });

    // Always derive next_season from current_season — single source of truth.
    const deriveNext = (cur) => {
        try {
            const [start] = cur.split("-");
            const y = parseInt(start, 10) + 1;
            return `${y}-${String(y + 1).slice(2)}`;
        } catch { return cur; }
    };

    // Sync fetched server settings into the editable form when they load/change.
    // Render-time adjustment (no setState-in-effect) — react-query keeps `data`
    // referentially stable, so this runs only when the settings actually change.
    const [syncedFrom, setSyncedFrom] = useState(null);
    if (data && data !== syncedFrom) {
        setSyncedFrom(data);
        setForm({
            current_season: data.current_season,
            next_season: deriveNext(data.current_season),
            season_status: data.season_status || "Offseason",
            current_stage: data.current_stage || "regular",
            team_bar_position: data.team_bar_position === "inline" ? "inline" : "subnav",
            expansion_now: !!data.expansion_now,
            site_logo_url: data.site_logo_url || "",
            logo_x: data.logo_x ?? 50,
            logo_y: data.logo_y ?? 50,
            logo_zoom: data.logo_zoom ?? 1,
            logo_w: data.logo_w ?? 96,
            logo_h: data.logo_h ?? 96,
            site_name: data.site_name || "Rosterly",
            site_tagline: data.site_tagline ?? "Women's Hockey",
            hero_title: data.hero_title || "",
            hero_title_colors: data.hero_title_colors || [],
            hero_subtitle: data.hero_subtitle || "",
            hero_image_url: data.hero_image_url || "",
            hero_variant: data.hero_variant || "original",
            hero_stats: (data.hero_stats && data.hero_stats.length) ? data.hero_stats : HERO_STATS_DEFAULT,
            hero_config: { ...DEFAULT_HERO_CONFIG, ...(data.hero_config || {}) },
            quick_links: (data.quick_links && data.quick_links.length) ? data.quick_links : DEFAULT_QUICK_LINKS,
            instagram_url: data.instagram_url || "",
            beta_mode: data.beta_mode !== false,
            beta_message: data.beta_message || "",
        });
    }

    const setCurrentSeason = (v) => {
        setForm((f) => ({ ...f, current_season: v, next_season: deriveNext(v) }));
    };

    const save = useMutation({
        mutationFn: () => api.put("/admin/settings", form).then((r) => r.data),
        onSuccess: () => {
            setError("");
            qc.invalidateQueries({ queryKey: ["settings"] });
            setSavedAt(new Date().toISOString());
        },
        onError: (e) => setError(e?.response?.data?.detail || "Save failed"),
    });

    if (isLoading) return <Loader />;

    const HERO_DEFAULT_COLORS = ["#FFFFFF", "#FFFFFF", "#9D4CDD"];
    const heroColorLines = form.hero_title
        ? form.hero_title.split("\n")
        : ["Every Player.", "Every Stat.", "Women's Hockey."];
    const setHeroLineColor = (i, color) => {
        const arr = [...(form.hero_title_colors || [])];
        while (arr.length <= i) arr.push("");
        arr[i] = color;
        setForm({ ...form, hero_title_colors: arr });
    };

    return (
        <div className="space-y-6" data-testid="settings-admin">
            <div className="rosterly-card p-6 md:p-7 space-y-5" data-testid="settings-beta-card">
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#9D4CDD]/12 border border-[#9D4CDD]/25 shrink-0"><SettingsIcon className="h-5 w-5 text-[#9D4CDD]" /></span>
                    <div>
                        <h3 className="font-heading text-xl font-bold uppercase tracking-tight leading-none">Beta Mode</h3>
                        <p className="text-[11px] text-[#64748B] mt-1">Control the site-wide Beta badge and footer notice.</p>
                    </div>
                </div>
                <label className="inline-flex items-start gap-3 cursor-pointer select-none" data-testid="setting-beta-mode-label">
                    <input
                        type="checkbox"
                        checked={form.beta_mode}
                        onChange={(e) => setForm({ ...form, beta_mode: e.target.checked })}
                        className="sr-only peer"
                        data-testid="setting-beta-mode"
                    />
                    <span className="relative inline-block w-11 h-6 rounded-full bg-[#1E2233] peer-checked:bg-[#9D4CDD] transition-colors flex-shrink-0 mt-0.5">
                        <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform peer-checked:translate-x-5"></span>
                    </span>
                    <span>
                        <span className="font-bold text-sm uppercase tracking-wider text-white">Show site-wide Beta bar &amp; badge</span>
                        <p className="text-xs text-[#94A3B8] mt-0.5 max-w-prose">
                            When ON, a "Beta" badge shows next to the logo and an info bar appears in the footer explaining that Rosterly is in Beta.
                        </p>
                    </span>
                </label>
                <Field label="Beta bar message (blank = default)">
                    <textarea
                        value={form.beta_message}
                        onChange={(e) => setForm({ ...form, beta_message: e.target.value })}
                        rows={2}
                        placeholder="Rosterly is currently in Beta. This Beta covers the 2026–27 PWHL season."
                        className={inputCls}
                        data-testid="setting-beta-message"
                    />
                </Field>
                <div className="flex items-center gap-3 border-t border-[#1E2233] pt-4">
                    <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-2" data-testid="save-beta-btn">
                        <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save beta settings"}
                    </button>
                </div>
            </div>

            <div className="rosterly-card p-6 md:p-7 space-y-5" data-testid="settings-branding-card">
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#9D4CDD]/12 border border-[#9D4CDD]/25 shrink-0"><Star className="h-5 w-5 text-[#9D4CDD]" /></span>
                    <div>
                        <h3 className="font-heading text-xl font-bold uppercase tracking-tight leading-none">Brand &amp; Logo</h3>
                        <p className="text-[11px] text-[#64748B] mt-1">Name, tagline and the home-button logo shown across the site.</p>
                    </div>
                </div>
                <p className="text-sm text-[#94A3B8]">
                    Customise the name shown next to the logo and the small tagline beneath it. These appear in the top navigation and footer.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Site Name (next to logo)">
                        <input
                            data-testid="setting-site-name"
                            value={form.site_name}
                            onChange={(e) => setForm({ ...form, site_name: e.target.value })}
                            className={inputCls}
                            placeholder="Rosterly"
                        />
                    </Field>
                    <Field label="Tagline (under the name)">
                        <input
                            data-testid="setting-site-tagline"
                            value={form.site_tagline}
                            onChange={(e) => setForm({ ...form, site_tagline: e.target.value })}
                            className={inputCls}
                            placeholder="Women's Hockey"
                        />
                    </Field>
                    <Field label="Instagram URL (header icon)">
                        <input
                            data-testid="setting-instagram-url"
                            value={form.instagram_url}
                            onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                            className={inputCls}
                            placeholder="https://www.instagram.com/your_handle/"
                        />
                    </Field>
                </div>
                <div className="border-t border-[#1E2233] pt-4">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD]">Home button style</span>
                    <p className="text-[11px] text-[#64748B] mt-1 mb-2.5">Choose what the top-left home button shows. Applies site-wide.</p>
                    <div className="flex flex-wrap gap-2" data-testid="logo-mode-selector">
                        {[
                            { v: "full", label: "Uploaded Logo" },
                            { v: "wordmark", label: "Site Name (text)" },
                            { v: "brand_logo", label: "Rosterly Logo" },
                        ].map((o) => {
                            const active = (data?.logo_mode || "full") === o.v;
                            return (
                                <button key={o.v} type="button" data-testid={`logo-mode-${o.v}`}
                                    onClick={() => saveLogoMode(o.v).then(() => qc.invalidateQueries({ queryKey: ["settings"] }))}
                                    className={`px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${active ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-[#0E0F16] border-[#1E2233] text-[#94A3B8] hover:border-[#9D4CDD]/50"}`}>
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <p className="text-sm text-[#94A3B8] border-t border-[#1E2233] pt-4">
                    Upload a logo shown in the top navigation (used by the <strong className="text-white">Uploaded Logo</strong> style). Leave empty to use the default Rosterly logo. Use the <strong className="text-white">crop &amp; zoom</strong> controls below to frame it exactly as it appears in the header.
                </p>
                <div className="flex items-start gap-5 flex-wrap">
                    {/* Live preview matching the header badge exactly */}
                    <div className="flex flex-col items-center gap-1.5">
                        <span className="rounded-md overflow-hidden bg-[#0A0B10] inline-flex items-center justify-center shrink-0 border border-[#1E2233]" style={{ width: `${Math.max(24, Math.min(140, form.logo_w))}px`, height: `${Math.max(24, Math.min(80, form.logo_h))}px` }}>
                            <img
                                src={form.site_logo_url ? assetUrl(form.site_logo_url) : "/rosterly-logo.png?v=4"}
                                alt="Logo preview"
                                className="w-full h-full object-contain"
                                style={{ objectPosition: `${form.logo_x}% ${form.logo_y}%`, transform: `scale(${form.logo_zoom})` }}
                                data-testid="settings-logo-preview"
                            />
                        </span>
                        <span className="text-[9px] uppercase tracking-wider text-[#64748B] font-bold">Header preview</span>
                    </div>
                    <div className="space-y-2">
                        <ImageUpload
                            value={form.site_logo_url}
                            onChange={(url) => setForm({ ...form, site_logo_url: url })}
                            label="Upload logo"
                            testId="settings-logo-upload"
                            size={72}
                            recommended="square works best, but any image can be cropped below"
                            allowUrl
                        />
                        <button type="button" onClick={() => setForm({ ...form, site_logo_url: "", logo_x: 50, logo_y: 50, logo_zoom: 1 })} className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] hover:text-white" data-testid="settings-logo-reset">
                            Reset to default logo
                        </button>
                    </div>
                    {/* Crop & zoom sliders */}
                    <div className="space-y-3 min-w-[220px] flex-1">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD]">
                            <SlidersHorizontal className="h-3.5 w-3.5" /> Adjust &amp; Crop
                        </div>
                        <LogoSlider label="Width" testId="logo-w-slider" min={40} max={400} step={2} value={form.logo_w}
                            display={`${Math.round(form.logo_w)}px`}
                            onChange={(v) => setForm({ ...form, logo_w: v })} />
                        <LogoSlider label="Height" testId="logo-h-slider" min={40} max={200} step={2} value={form.logo_h}
                            display={`${Math.round(form.logo_h)}px`}
                            onChange={(v) => setForm({ ...form, logo_h: v })} />
                        <LogoSlider label="Zoom" testId="logo-zoom-slider" min={1} max={3} step={0.05} value={form.logo_zoom}
                            display={`${Number(form.logo_zoom).toFixed(2)}×`}
                            onChange={(v) => setForm({ ...form, logo_zoom: v })} />
                        <LogoSlider label="Horizontal" testId="logo-x-slider" min={0} max={100} step={1} value={form.logo_x}
                            display={`${Math.round(form.logo_x)}%`}
                            onChange={(v) => setForm({ ...form, logo_x: v })} />
                        <LogoSlider label="Vertical" testId="logo-y-slider" min={0} max={100} step={1} value={form.logo_y}
                            display={`${Math.round(form.logo_y)}%`}
                            onChange={(v) => setForm({ ...form, logo_y: v })} />
                        <button type="button" onClick={() => setForm({ ...form, logo_x: 50, logo_y: 50, logo_zoom: 1, logo_w: 96, logo_h: 96 })} className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] hover:text-white" data-testid="logo-crop-reset">
                            Reset size &amp; crop
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-3 border-t border-[#1E2233] pt-4">
                    <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-2" data-testid="save-logo-btn">
                        <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save branding"}
                    </button>
                </div>
            </div>

        </div>
    );
}

export default SettingsAdmin;